import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ClipboardCheck, ArrowLeft, Check, Star, Zap, Shield, Smartphone } from 'lucide-react';

export default function Pricing() {
  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center mb-4">
            <ClipboardCheck className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-foreground">QA Builder Pro</h1>
          <p className="text-muted-foreground mt-1">Simple, Transparent Pricing</p>
        </div>

        {/* Back Button */}
        <div className="mb-6">
          <Link to="/register">
            <Button variant="ghost" size="sm" data-testid="back-to-register-btn">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Register
            </Button>
          </Link>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {/* Monthly Plan */}
          <Card className="border-2 hover:border-primary/50 transition-colors">
            <CardHeader className="text-center pb-4">
              <div className="flex justify-center mb-2">
                <Badge variant="outline" className="text-base px-4 py-1">Monthly</Badge>
              </div>
              <CardTitle className="text-4xl font-bold">£3</CardTitle>
              <CardDescription className="text-base">per month</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <Feature text="Unlimited QA forms" />
                <Feature text="All 13 trade templates" />
                <Feature text="Photo documentation" />
                <Feature text="Digital signatures" />
                <Feature text="PDF export with watermark" />
                <Feature text="Offline mode with sync" />
                <Feature text="Team collaboration" />
                <Feature text="Cancel anytime" />
              </div>
              <Link to="/register" className="block">
                <Button variant="outline" className="w-full h-12 text-base mt-4" data-testid="monthly-cta">
                  Get Started - £3/month
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* Lifetime Plan */}
          <Card className="border-2 border-primary relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <Badge className="bg-green-600 text-white px-4 py-1">
                <Star className="h-3 w-3 mr-1 inline" />
                Best Value
              </Badge>
            </div>
            <CardHeader className="text-center pb-4 pt-8">
              <div className="flex justify-center mb-2">
                <Badge className="bg-green-100 text-green-700 text-base px-4 py-1">Lifetime</Badge>
              </div>
              <CardTitle className="text-4xl font-bold text-green-600">£10</CardTitle>
              <CardDescription className="text-base">one-time payment</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <Feature text="Everything in Monthly" />
                <Feature text="Unlimited QA forms forever" />
                <Feature text="All future updates included" />
                <Feature text="Priority support" />
                <Feature text="No recurring charges" />
                <Feature text="Pay once, use forever" highlight />
              </div>
              <Link to="/register" className="block">
                <Button className="w-full h-12 text-base mt-4 bg-green-600 hover:bg-green-700" data-testid="lifetime-cta">
                  <Zap className="h-4 w-4 mr-2" />
                  Get Lifetime Access - £10
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>

        {/* Features Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-xl text-center">Everything You Need for Construction QA</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <ClipboardCheck className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">Digital QA Forms</h3>
                <p className="text-sm text-muted-foreground">
                  Create professional inspection checklists for any construction trade
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Smartphone className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">Works Offline</h3>
                <p className="text-sm text-muted-foreground">
                  Use on-site without internet, syncs automatically when connected
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Shield className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">Approval Workflow</h3>
                <p className="text-sm text-muted-foreground">
                  Submit, review, approve or return forms with full audit trail
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* FAQ */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Frequently Asked Questions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h4 className="font-semibold mb-1">Can I try before I buy?</h4>
              <p className="text-sm text-muted-foreground">
                Yes! You get a 7-day free trial with full access to all features.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-1">What payment methods do you accept?</h4>
              <p className="text-sm text-muted-foreground">
                We accept all major credit/debit cards through our secure payment processor, Paddle.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-1">What is your refund policy?</h4>
              <p className="text-sm text-muted-foreground">
                We offer a 14-day refund guarantee on all plans in accordance with Paddle's Buyer Terms. See our <Link to="/refund" className="text-primary hover:underline">Refund Policy</Link> for details.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-1">Can I cancel my subscription?</h4>
              <p className="text-sm text-muted-foreground">
                Yes, monthly subscriptions can be cancelled anytime. You'll retain access until the end of your billing period.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-1">Is my data secure?</h4>
              <p className="text-sm text-muted-foreground">
                Absolutely. All data is encrypted in transit and at rest. We never share your data with third parties.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Footer Links */}
        <div className="mt-8 text-center space-x-4">
          <Link to="/register" className="text-primary hover:underline text-sm font-medium">
            Create Account
          </Link>
          <span className="text-muted-foreground">|</span>
          <Link to="/login" className="text-primary hover:underline text-sm font-medium">
            Sign In
          </Link>
          <span className="text-muted-foreground">|</span>
          <Link to="/terms" className="text-primary hover:underline text-sm font-medium">
            Terms
          </Link>
          <span className="text-muted-foreground">|</span>
          <Link to="/privacy" className="text-primary hover:underline text-sm font-medium">
            Privacy
          </Link>
          <span className="text-muted-foreground">|</span>
          <Link to="/refund" className="text-primary hover:underline text-sm font-medium">
            Refund Policy
          </Link>
        </div>
      </div>
    </div>
  );
}

function Feature({ text, highlight }) {
  return (
    <div className="flex items-center gap-3">
      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${highlight ? 'bg-amber-100' : 'bg-green-100'}`}>
        <Check className={`h-3 w-3 ${highlight ? 'text-amber-600' : 'text-green-600'}`} />
      </div>
      <span className={`text-sm ${highlight ? 'font-medium text-amber-700' : ''}`}>{text}</span>
    </div>
  );
}
